import { CoreButton, CoreInput, CoreModalDialogFooter, CoreSelect } from '@components'
import CalendarIcon from '@images/icons/filter-calendar.svg?react'
import { toastSuccess } from '@utils'
import {
  isOwnerOrAdministrator,
  paths,
  selectCurrentUserId,
  selectCurrentUserRole,
  useAppSelector,
  useCreateReportMutation,
  useDepartmentOptionsQry,
  useMerchantQry,
  useUpdateReportMutation
} from 'mmfintech-backend-api'
import type {
  ApprovalMatrixLevelApproverResponse,
  DepartmentResponse,
  ReportRequest,
  ReportResponse
} from 'mmfintech-backend-api/types'
import { fixDateOnly, isValidArray, tr, useFormValues } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { type BaseSyntheticEvent, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import './reportEditModal.scss'

interface ReportEditProps {
  report?: ReportResponse
  onClose: () => void
  reload?: () => void
}

export const ReportEditModal = ({ report, onClose, reload }: ReportEditProps) => {
  const history = useHistory()

  const userId = useAppSelector(selectCurrentUserId)
  const customerRole = useAppSelector(selectCurrentUserRole)
  const [error, setError] = useState('')

  const { merchant } = useMerchantQry()
  const { departments, departmentsOptions } = useDepartmentOptionsQry()

  const [createReportRequest, { isLoading: createIsLoading, error: createError }] = useCreateReportMutation({
    fixedCacheKey: 'refetch-reports'
  })
  const [editReportRequest, { isLoading: editIsLoading, error: editError }] = useUpdateReportMutation({
    fixedCacheKey: 'refetch-reports'
  })

  const formValues = useFormValues({
    name: { required: true, validation: 'safe-string', value: report?.name },
    description: { validation: 'safe-string', value: report?.description },
    fromDate: { value: report?.fromDate },
    toDate: { value: report?.toDate },
    departmentId: { value: report?.department?.id }
  })

  useEffect(() => {
    if (userId && !report && isValidArray(departments)) {
      formValues.setValue(
        'departmentId',
        departments.find((d: DepartmentResponse) => d.departmentUsers.find(u => u.id === userId))?.id
      )
    }
  }, [departments, userId, report])

  useEffect(() => {
    if (formValues.getValue('departmentId') && departments) {
      const department = departments?.find((d: DepartmentResponse) => d.id === formValues.getValue('departmentId'))
      const isUserFinalApprover = department?.approvalMatrix?.approvalLevels[
        department.approvalMatrix.approvalLevels.length - 1
      ].approvers.find((a: ApprovalMatrixLevelApproverResponse) => a.approverUser.id === userId)

      if (isUserFinalApprover && !isOwnerOrAdministrator(customerRole)) {
        setError(
          tr(
            'FRONTEND.REPORTS.CREATE.USER_IS_FINAL_APPROVER',
            'You cannot submit a report for this department because you are the final approval in its matrix. Only administrators have permission to do so.'
          )
        )
      } else {
        setError('')
      }
    }
  }, [departments, formValues.getValue('departmentId')])

  const createReport = async (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (formValues.areValid()) {
      try {
        const data = {
          ...formValues.prepare(),
          merchantName: merchant?.name,
          fromDate: fixDateOnly(formValues.getValue('fromDate')),
          toDate: fixDateOnly(formValues.getValue('toDate'))
        } as ReportRequest

        if (report?.id) {
          await editReportRequest({
            ...data,
            reportId: report?.id
          }).unwrap()
          toastSuccess(tr('FRONTEND.REPORTS.CREATE.EDITED_SUCCESSFULLY', 'Report edited successfully'))
          reload?.()
          onClose()
        } else {
          const res = await createReportRequest(data).unwrap()
          toastSuccess(tr('FRONTEND.REPORTS.CREATE.CREATED_SUCCESSFULLY', 'Report created successfully'))
          reload?.()
          onClose()
          history.push(paths.expenseManagement.reportDetails(res.id))
        }
      } catch (err) {
        formValues.handleErrors(err)
      }
    }
  }

  const buttonText = report?.id
    ? tr('FRONTEND.REPORTS.CREATE.SAVE', 'Save')
    : tr('FRONTEND.REPORTS.CREATE.CREATE_REPORT', 'Create Report')

  return (
    <div className='report-edit-modal-container'>
      <div className='form'>
        <CoreInput
          type='text'
          {...formValues.registerInput('name')}
          label={tr('FRONTEND.REPORTS.CREATE.REPORT_NAME', 'Report Name')}
        />
        <div className='col'>
          <CoreInput
            type='date'
            {...formValues.registerInput('fromDate')}
            label={tr('FRONTEND.REPORTS.CREATE.FROM', 'From')}
            LeftIcon={<CalendarIcon />}
            maxDate={formValues.getValue('toDate')}
            // parentClassName='calendar-input'
            dateFormat='dd/MM/yyyy'
            dataPickerForwardYears={5}
            rangeDates
            selectsStart
            popperPlacement='bottom-start'
          />
          <CoreInput
            type='date'
            {...formValues.registerInput('toDate')}
            label={tr('FRONTEND.REPORTS.CREATE.TO', 'To')}
            LeftIcon={<CalendarIcon />}
            minDate={formValues.getValue('fromDate')}
            // parentClassName='calendar-input'
            dateFormat='dd/MM/yyyy'
            dataPickerForwardYears={5}
            rangeDates
            selectsEnd
            popperPlacement='bottom-end'
          />
        </div>

        <CoreSelect
          type='default'
          data-test='departmentId'
          options={departmentsOptions}
          label={tr('FRONTEND.REPORTS.CREATE.DEPARTMENT', 'Department')}
          {...formValues.registerInput('departmentId')}
          clearLabel={tr('FRONTEND.REPORTS.CREATE.CLEAR_DEPARTMENT', 'Clear selection')}
        />

        <CoreInput
          type='text'
          {...formValues.registerInput('description')}
          label={tr('FRONTEND.REPORTS.CREATE.DESCRIPTION', 'Description')}
        />
      </div>
      <ErrorDisplay error={createError || editError || error} />
      <CoreModalDialogFooter>
        <div className='buttons-container'>
          <CoreButton
            data-test='create-edit-report'
            variation='primary-black'
            isLoading={createIsLoading || editIsLoading}
            text={buttonText}
            disabled={!!error || !formValues.getValue('name')}
            onClick={createReport}
            size='large'
            fullWidth
          />
          <CoreButton
            variation='tertiary'
            data-test='close-edit-report'
            disabled={createIsLoading || editIsLoading}
            text={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            onClick={onClose}
            size='large'
          />
        </div>
      </CoreModalDialogFooter>
    </div>
  )
}
